import { useState, useEffect } from 'react';
import { NavLink, useLocation, Link, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    Phone,
    Calendar,
    GitBranch,
    Mail,
    Mailbox as MailboxIcon,
    CreditCard,
    Settings,
    Zap,
    MessageSquare,
    ChevronLeft,
    ChevronRight,
    Menu,
    X,
    BarChart,
    Star,
    Share2,
    FileText,
    UserPlus,
    TrendingUp
} from 'lucide-react';

const navItems = [
    {
        section: 'Overview',
        items: [
            { path: '/', label: 'Dashboard', icon: LayoutDashboard },
            { path: '/usage', label: 'Usage', icon: BarChart },
        ]
    },
    {
        section: 'Communication',
        items: [
            { path: '/contacts', label: 'Customers', icon: Users },
            { path: '/inbox', label: 'Inbox', icon: MessageSquare },
            { path: '/mailbox', label: 'Email', icon: MailboxIcon },
            { path: '/voice-ai', label: 'Voice AI', icon: Phone },
            { path: '/calendar', label: 'Calendar', icon: Calendar },
            { path: '/invoices', label: 'Invoices', icon: FileText },
        ]
    },
    {
        section: 'Marketing',
        items: [
            { path: '/reviews', label: 'Reviews', icon: Star },
            { path: '/campaigns', label: 'Campaigns', icon: Mail },
            { path: '/social', label: 'Social Media', icon: Share2 },
            { path: '/pipeline', label: 'Pipeline', icon: GitBranch },
        ]
    },
    {
        section: 'Account',
        forBusinessUsers: true,
        items: [
            { path: '/billing', label: 'Billing', icon: CreditCard },
            { path: '/team', label: 'Team', icon: Users },
            { path: '/settings', label: 'Settings', icon: Settings },
        ]
    },
    {
        section: 'Admin',
        forAgencyOnly: true,
        items: [
            { path: '/admin', label: 'Admin Dashboard', icon: LayoutDashboard },
            { path: '/admin/revenue', label: 'Financials', icon: TrendingUp },
            { path: '/admin/leads', label: 'Demo Leads', icon: UserPlus },
            { path: '/admin/whatsapp-templates', label: 'WA Templates', icon: MessageSquare },
            { path: '/team', label: 'Team', icon: Users },
            { path: '/settings', label: 'Settings', icon: Settings },
        ]
    }
];

import { useAuthStore } from '../stores/authStore';
import { useMessagingStore } from '../stores/messagingStore';
import { useBusinessSettings } from '../stores/businessSettingsStore';

export default function Sidebar() {
    const location = useLocation();
    const navigate = useNavigate();
    const { isAgencyAdmin, isOwner, user, token } = useAuthStore();
    const { unreadCount, fetchUnreadCount } = useMessagingStore();
    const { features, hasFeature, fetchSettings } = useBusinessSettings();
    const [collapsed, setCollapsed] = useState(window.innerWidth < 768);

    // Initial Unread Count & Polling
    useEffect(() => {
        if (!token) return;

        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 10000); // 10s polling
        return () => clearInterval(interval);
    }, [token, fetchUnreadCount]);

    // Auto-collapse on mobile when navigating
    useEffect(() => {
        if (window.innerWidth < 768) {
            setCollapsed(true);
        }
    }, [location]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            // Tablet/Desktop check
            if (window.innerWidth < 1024) {
                setCollapsed(true);
            } else {
                setCollapsed(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch Business Settings for Gating (via shared store)
    useEffect(() => {
        if (token) fetchSettings();
    }, [token, location.search, user?.business_id, fetchSettings]);

    const toggleSidebar = () => setCollapsed(!collapsed);

    return (
        <>
            {/* Mobile Toggle Button */}
            <button
                className="mobile-menu-btn"
                onClick={toggleSidebar}
                style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    zIndex: 1000,
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    background: 'var(--accent-gradient)',
                    color: 'white',
                    border: 'none',
                    boxShadow: 'var(--shadow-lg)',
                    display: 'none', // mobile.css overrides to flex; hide-on-inbox keeps it hidden
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                }}
            >
                {collapsed ? <Menu size={24} /> : <X size={24} />}
            </button>

            <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-header">
                    {!collapsed ? (
                        <div className="sidebar-brand">
                            <img src="/branding/logo-dark.png" alt="AlwaysIn" className="sidebar-brand-img logo-dark" style={{ height: '40px' }} />
                            <img src="/branding/logo-light.png" alt="AlwaysIn" className="sidebar-brand-img logo-light" style={{ height: '40px' }} />
                        </div>
                    ) : null}

                    <button
                        onClick={toggleSidebar}
                        className="btn btn-icon btn-ghost"
                        style={{
                            marginLeft: 'auto',
                            display: window.innerWidth < 768 ? 'none' : 'flex'
                        }}
                    >
                        {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                    </button>
                </div>

                <nav className="sidebar-nav">
                    {navItems.map((section, idx) => {
                        const isPlatformOwner = isAgencyAdmin(); // 'owner' role
                        const isImpersonating = new URLSearchParams(location.search).has('business_id');

                        // Agency-only sections: only show to platform owner NOT impersonating
                        if (section.forAgencyOnly && (!isPlatformOwner || isImpersonating)) return null;

                        // Business user sections: show to non-owners OR impersonating owners
                        if (section.forBusinessUsers && isPlatformOwner && !isImpersonating) return null;

                        // Main + Business: hide from non-impersonating platform owners
                        if (!section.forAgencyOnly && !section.forBusinessUsers && isPlatformOwner && !isImpersonating) return null;

                        return (
                            <div key={idx} className="nav-section">
                                {!collapsed && (
                                    <div className="nav-section-title animate-fade-in">
                                        {section.section}
                                    </div>
                                )}
                                {section.items.map((item) => {
                                    // FEATURE GATING — uses FeatureGate-resolved features
                                    if (features) {
                                        if (item.path === '/inbox' && !hasFeature('sms') && !hasFeature('sms_ai')) return null;
                                        if (item.path === '/voice-ai' && !hasFeature('voice_ai')) return null;
                                        if (item.path === '/reviews' && !hasFeature('automatic_review_responses')) return null;
                                        if (item.path === '/campaigns' && !hasFeature('campaigns')) return null;
                                        if (item.path === '/pipeline' && !hasFeature('pipeline')) return null;
                                        if (item.path === '/invoices' && !hasFeature('invoicing')) return null;
                                        if (item.path === '/social' && !hasFeature('social_media')) return null;
                                        if (item.path === '/team' && !hasFeature('team')) return null;
                                    }

                                    const Icon = item.icon;
                                    const isActive = location.pathname === item.path;

                                    // Preserve business_id query param when impersonating
                                    const urlParams = new URLSearchParams(location.search);
                                    const businessId = urlParams.get('business_id');
                                    const linkTo = businessId ? `${item.path}?business_id=${businessId}` : item.path;

                                    return (
                                        <Link
                                            key={item.path}
                                            to={linkTo}
                                            className={`nav-link ${isActive ? 'active' : ''}`}
                                            title={collapsed ? item.label : ''}
                                            style={{ position: 'relative' }}
                                        >
                                            <Icon size={20} />
                                            {!collapsed && (
                                                <span className="animate-fade-in">{item.label}</span>
                                            )}

                                            {item.path === '/inbox' && unreadCount > 0 && (
                                                <span className="badge" style={{
                                                    position: 'absolute',
                                                    right: collapsed ? '4px' : '16px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    background: 'var(--accent-primary)',
                                                    color: 'white',
                                                    borderRadius: '10px',
                                                    padding: '2px 6px',
                                                    fontSize: '10px',
                                                    fontWeight: 700,
                                                    minWidth: '18px',
                                                    textAlign: 'center',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                                }}>
                                                    {unreadCount > 99 ? '99+' : unreadCount}
                                                </span>
                                            )}
                                        </Link>
                                    );
                                })}
                            </div>
                        )
                    })}
                </nav>

                {/* Help Section at Bottom */}
                <div style={{
                    padding: 'var(--spacing-4)',
                    borderTop: '1px solid var(--border-primary)',
                    marginTop: 'auto',
                    display: collapsed ? 'none' : 'block'
                }}>
                    <Link to="/help" style={{ textDecoration: 'none' }}>
                        <div className="card" style={{
                            padding: 'var(--spacing-4)',
                            background: 'var(--accent-gradient)',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            transition: 'transform 0.15s, box-shadow 0.15s'
                        }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(99,102,241,0.3)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                        >
                            <p style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-2)', fontWeight: 600 }}>
                                Need help?
                            </p>
                            <p style={{ fontSize: 'var(--font-size-xs)', opacity: 0.9 }}>
                                Contact support or browse our docs.
                            </p>
                        </div>
                    </Link>
                </div>
            </aside>
        </>
    );
}
